import { DfVehicleStatus } from "../../shared-logic/utils/dfConstants";
import type { UscStateType } from "../redux/store";
import type { UscAccessoryType, UscPackType } from "./accessoryUtils";
import { getFeeListSelector, getServicesListSelector } from "./externalPriceUtils";

export type BasketConfigurationType = {
    accessories: UscAccessoryType[];
    packs: UscPackType[];
    services: unknown[];
};

/**
 * Selector which returns the presence of an accessory item in the configuration.
 */
export const isAccessoryInBasketSelector =
    (accessoryId: string) =>
    (state: UscStateType): boolean =>
        state.basket.configuration.accessories.some((accessory) => accessory.id === accessoryId);

/**
 * Selector which returns the presence of a pack item in the configuration.
 */
export const isPackInBasketSelector =
    (packId: string) =>
    (state: UscStateType): boolean =>
        state.basket.configuration.packs.some((pack) => pack.id === packId);

/**
 * Helper function to check if there are finance services to show in the basket
 */
const hasFinanceServicesToShow = (state: UscStateType): boolean => {
    const { basket, finance } = state;
    if (basket.activeFinanceOption === "cash" || finance.services.length === 0) return false;

    // A service is considered to be shown if it is selected by the user. We intentionally do not include
    // included in price services here as the user did not "actively" select them.
    return finance.services.some((service) => service.selected);
};

/**
 * Selector which returns if we should be showing the basket price breakdown.
 */
export const shouldShowPriceBreakdownSelector = (state: UscStateType): boolean => {
    // Never show the price breakdown if the vehicle is not available
    const { result: vehicleResult } = state.vehicle;
    if (vehicleResult?.vehicleStatus.code !== DfVehicleStatus.Available) return false;

    const { accessories, packs } = state.basket.configuration;
    const { basketConfig } = state.uscSettings;
    const { showTaxesAndCosts, showAdditionalCosts } = basketConfig;
    const hasFinanceServices = hasFinanceServicesToShow(state);
    const services = getServicesListSelector("included")(state);
    const filteredServices = services.filter((service) => !service.preSelected);
    const basketFees = getFeeListSelector("included")(state);
    const basketAdditionalCosts = getFeeListSelector("excluded")(state);

    const hasAccessoriesOrPacksInBasket = !!(accessories.length || packs.length);
    const hasServicesInBasket = !!(filteredServices.length || hasFinanceServices);
    const hasFeesInBasket = !!basketFees.length;
    const hasAdditionalCostsInBasket = !!basketAdditionalCosts.length;

    return (
        hasAccessoriesOrPacksInBasket ||
        hasServicesInBasket ||
        (showTaxesAndCosts && hasFeesInBasket) ||
        (showAdditionalCosts && hasAdditionalCostsInBasket)
    );
};

/**
 * Selector which returns the amount of items in the basket. This takes into account
 * the accessories, packs, services and fees - and whether or not they are included in the price.
 */
export const getBasketItemsCountSelector = (state: UscStateType): number => {
    const { accessories, packs } = state.basket.configuration;
    const { basketConfig } = state.uscSettings;
    const { finance } = state;
    const hasFinanceServices = hasFinanceServicesToShow(state);
    const services = getServicesListSelector("included")(state);
    const filteredServices = services.filter((service) => !service.preSelected);
    const basketFees = getFeeListSelector("included")(state);
    const basketAdditionalCosts = getFeeListSelector("excluded")(state);

    return (
        accessories.length +
        packs.length +
        filteredServices.length +
        (hasFinanceServices ? finance.services.length : 0) +
        (basketConfig.showTaxesAndCosts && basketFees.length ? basketFees.length : 0) +
        (basketConfig.showAdditionalCosts && basketAdditionalCosts.length ? basketAdditionalCosts.length : 0)
    );
};

/**
 * Specifically made for the USC Debug modal (so that we don't have to repeat all the selectors above),
 * this selector returns the "reasons" for which we should or shouldn't show the price breakdown.
 * In the Debug Modal, we should just be able to map over each item which is either true/false
 * If all are true, we show the price breakdown
 */
export const shouldShowPriceBreakdownDebugSelector = (
    state: UscStateType,
): Record<string, { title: string; enabled: boolean }> => {
    const { accessories, packs } = state.basket.configuration;
    const { basketConfig } = state.uscSettings;
    const { showTaxesAndCosts, showAdditionalCosts } = basketConfig;
    const hasFinanceServices = hasFinanceServicesToShow(state);
    const services = getServicesListSelector("included")(state);
    const filteredServices = services.filter((service) => !service.preSelected);
    const basketFees = getFeeListSelector("included")(state);
    const basketAdditionalCosts = getFeeListSelector("excluded")(state);

    const hasAccessoriesOrPacksInBasket = !!(accessories.length || packs.length);
    const hasServicesInBasket = !!(filteredServices.length || hasFinanceServices);
    const hasFeesInBasket = !!basketFees.length;
    const hasAdditionalCostsInBasket = !!basketAdditionalCosts.length;

    return {
        accessories: {
            title: "Accessories or Packs",
            enabled: hasAccessoriesOrPacksInBasket,
        },
        services: {
            title: "Services",
            enabled: hasServicesInBasket,
        },
        fees: {
            title: "Fees (Taxes and Costs)",
            enabled: showTaxesAndCosts && hasFeesInBasket,
        },
        additionalCosts: {
            title: "Additional Costs",
            enabled: showAdditionalCosts && hasAdditionalCostsInBasket,
        },
    };
};
